import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { translationPropType, gettext } from '@eventbrite/i18n';
import { IconButton } from '@eventbrite/eds-icon-button';
import {
    STYLE_PROP_TYPE as ICON_BUTTON_STYLE_PROPTYPE,
    ICON_PROP_TYPE as ICON_BUTTON_ICON_PROPTYPE,
} from '@eventbrite/eds-icon-button';
import {
    JoyrideTourGuide,
    JOYRIDE_TOURGUIDE_STYLES_DEFAULT,
    JOYRIDE_TOURGUIDE_FLOATER_STYLES_DEFAULT,
} from '@eventbrite/marmalade';
import loadable from '@loadable/component';
import { MoreActions } from '@eventbrite/eds-more-actions';
import { ShareIosChunky, ThumbsDownChunky } from '@eventbrite/eds-iconography';
import { getTooltipContent } from '../getToolTipContent';

const LazyJoyride = loadable.lib(() => import('react-joyride'));
const EventCardAction = ({
    id,
    iconType,
    style,
    title,
    secondary,
    onClick,
    isListCard,
    showLikesTooltip,
    saved,
    showRemoveTooltip,
    showMoreActions,
    showFewerEventsIcon,
    onEventCardMoreAction,
    adjustPadding = false,
    isAuthenticated,
}) => {
    let component = null;
    const iconClassName = classNames(
        `eds-event-card__actions-id__${id}`,
        'eds-event-card-content__actions-container__action',
        {
            'eds-l-pad-all-1': !isListCard && !adjustPadding,
            'eds-l-pad-all-2': adjustPadding,
            'eds-l-mar-right-2': !!secondary && !isListCard,
            'eds-event-card-content__actions-icon--primary': !secondary,
            'eds-event-card-content__actions-icon--secondary': !!secondary,
        },
    );

    const showFewerEventsClasName = classNames(
        `eds-event-card__actions-id__${id}__more-actions`,
        'eds-event-card-content__actions-container__action',
        'eds-event-card-content__actions-icon--more-actions',
        'eds-l-pad-all-2',
    );

    const removeEventClassName = classNames(
        `eds-event-card__actions-id__${id}__more-actions`,
        'eds-event-card-content__actions-container__action',
        'eds-event-card-content__actions-icon--more-actions',
        `eds-event-card-actions-id__${id}__remove-action`,
        'eds-l-pad-all-2',
    );

    const moreActionsIconClassName = classNames(
        `eds-event-card__actions-id__${id}__more-actions`,
        'eds-event-card-content__actions-container__action',
        'eds-event-card-content__actions-icon--more-actions',
        {
            'eds-l-pad-all-1': !isListCard,
        },
    );

    const tooltipContent = getTooltipContent({ id, showLikesTooltip });

    if (iconType) {
        component = (
            <>
                {showLikesTooltip || (isAuthenticated && showRemoveTooltip) ? (
                    <LazyJoyride>
                        {({ default: Joyride }) => (
                            <Joyride
                                disableOverlay
                                disableScrolling
                                steps={[tooltipContent]}
                                tooltipComponent={JoyrideTourGuide}
                                locale={{
                                    back: '',
                                    last: showRemoveTooltip
                                        ? gettext('Got it').toString()
                                        : '',
                                }}
                                styles={JOYRIDE_TOURGUIDE_STYLES_DEFAULT}
                                floaterProps={{
                                    styles: JOYRIDE_TOURGUIDE_FLOATER_STYLES_DEFAULT,
                                }}
                            />
                        )}
                    </LazyJoyride>
                ) : null}

                <span
                    className={
                        showFewerEventsIcon
                            ? showFewerEventsClasName
                            : iconClassName
                    }
                >
                    <IconButton
                        aria-pressed={saved}
                        title={title}
                        style={style}
                        iconType={iconType}
                        onClick={(e) => {
                            if (showFewerEventsIcon) {
                                onEventCardMoreAction('shareEvent');
                            } else {
                                onClick(e);
                            }
                        }}
                        data-event-id={id}
                        data-testid={
                            showFewerEventsIcon && 'share-event-experiment'
                        }
                    />
                </span>

                {showFewerEventsIcon && isAuthenticated ? (
                    <span className={removeEventClassName}>
                        <IconButton
                            title={title}
                            iconType={<ThumbsDownChunky />}
                            onClick={() => {
                                onEventCardMoreAction('removeEvent');
                            }}
                            data-testid="remove-event-experiment"
                        />
                    </span>
                ) : null}

                {showMoreActions ? (
                    <span className={moreActionsIconClassName}>
                        <MoreActions
                            onAction={onEventCardMoreAction}
                            items={[
                                {
                                    value: 'shareEvent',
                                    content: gettext('Share'),
                                    iconType: <ShareIosChunky />,
                                },
                                {
                                    value: 'removeEvent',
                                    content: gettext(
                                        'Show fewer events like this',
                                    ),
                                    iconType: <ThumbsDownChunky />,
                                },
                            ]}
                        />
                    </span>
                ) : null}
            </>
        );
    }

    return component;
};

export default class EventCardActions extends PureComponent {
    static propTypes = {
        id: PropTypes.string,
        primaryIconType: ICON_BUTTON_ICON_PROPTYPE,
        primaryIconStyle: ICON_BUTTON_STYLE_PROPTYPE,
        primaryIconTitle: translationPropType,
        primaryIconOnClick: PropTypes.func,
        secondaryIconType: ICON_BUTTON_ICON_PROPTYPE,
        secondaryIconStyle: ICON_BUTTON_STYLE_PROPTYPE,
        secondaryIconTitle: translationPropType,
        secondaryIconOnClick: PropTypes.func,
        shouldShowIcons: PropTypes.bool,
        isListCard: PropTypes.bool,
        showLikesTooltip: PropTypes.bool,
        showRemoveTooltip: PropTypes.bool,
        showMoreActions: PropTypes.bool,
        showFewerEventsIcon: PropTypes.bool,
        onEventCardMoreAction: PropTypes.func,
    };

    render() {
        const {
            id,
            primaryIconType,
            primaryIconStyle,
            primaryIconTitle,
            primaryIconOnClick,
            secondaryIconType,
            secondaryIconStyle,
            secondaryIconTitle,
            secondaryIconOnClick,
            isListCard,
            shouldShowIcons,
            showLikesTooltip,
            saved,
            showRemoveTooltip,
            showMoreActions,
            showFewerEventsIcon,
            onEventCardMoreAction,
            isAuthenticated,
        } = this.props;
        const classes = classNames(
            'eds-event-card-content__actions-container eds-event-card-content__actions-container--consumer',
            {
                'eds-event-card-content__actions-container--always-visible eds-event-card-content__actions-container--display-flex':
                    shouldShowIcons,
            },
        );

        return (
            <div className={classes}>
                {showFewerEventsIcon ? (
                    <>
                        <EventCardAction
                            id={id}
                            iconType={primaryIconType}
                            style={primaryIconStyle}
                            title={primaryIconTitle}
                            onClick={primaryIconOnClick}
                            saved={saved}
                            adjustPadding={showFewerEventsIcon}
                        />
                        <EventCardAction
                            id={id}
                            iconType={secondaryIconType}
                            style={secondaryIconStyle}
                            title={secondaryIconTitle}
                            onClick={secondaryIconOnClick}
                            secondary={secondaryIconType}
                            isListCard={isListCard}
                            showLikesTooltip={showLikesTooltip}
                            showRemoveTooltip={showRemoveTooltip}
                            showMoreActions={showMoreActions}
                            showFewerEventsIcon={showFewerEventsIcon}
                            onEventCardMoreAction={onEventCardMoreAction}
                            saved={saved}
                            isAuthenticated={isAuthenticated}
                        />
                    </>
                ) : (
                    <>
                        <EventCardAction
                            id={id}
                            iconType={secondaryIconType}
                            style={secondaryIconStyle}
                            title={secondaryIconTitle}
                            onClick={secondaryIconOnClick}
                            secondary={secondaryIconType}
                            isListCard={isListCard}
                            showLikesTooltip={showLikesTooltip}
                            saved={saved}
                        />

                        <EventCardAction
                            id={id}
                            iconType={primaryIconType}
                            style={primaryIconStyle}
                            title={primaryIconTitle}
                            onClick={primaryIconOnClick}
                            isListCard={isListCard}
                            showLikesTooltip={showLikesTooltip}
                            showRemoveTooltip={showRemoveTooltip}
                            showMoreActions={showMoreActions}
                            onEventCardMoreAction={onEventCardMoreAction}
                            saved={saved}
                        />
                    </>
                )}
            </div>
        );
    }
}
